import type { RequestStatus } from "@procuremate/shared";
import { clsx } from "clsx";
import { Archive, CircleCheck, CircleX, PencilLine, Search, Send, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const STYLES: Record<RequestStatus, { className: string; Icon: typeof Send }> = {
  DRAFT: { className: "bg-bg text-fg-muted border border-border", Icon: PencilLine },
  SUBMITTED: { className: "bg-success-bg text-success", Icon: Send },
  IN_REVIEW: { className: "bg-warning-bg text-warning-fg", Icon: Search },
  RETURNED_TO_REQUESTER: { className: "bg-warning-bg text-warning-fg", Icon: Undo2 },
  SOURCING: { className: "bg-warning-bg text-warning-fg", Icon: Search },
  APPROVED: { className: "bg-success-bg text-success", Icon: CircleCheck },
  REJECTED: { className: "bg-danger-bg text-danger", Icon: CircleX },
  ABANDONED: { className: "bg-bg text-fg-muted border border-border", Icon: Archive },
};

/**
 * Request status as a pill: icon + text, never colour alone (BRAND.md). `audience="buyer"` uses the
 * buyer's words ("Returned to requester" instead of "Returned to you", WP-3.1).
 */
export function RequestStatusBadge({
  status,
  audience = "requester",
}: {
  status: RequestStatus;
  audience?: "requester" | "buyer";
}) {
  const { t } = useTranslation();
  const { className, Icon } = STYLES[status];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
        className,
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {audience === "buyer" ? t(`review.status.${status}`) : t(`requests.status.${status}`)}
    </span>
  );
}
